<template>
  <d-container
    fluid
    class="main-content-container px-0 px-md-2 px-lg-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        sm="12"
        md="9"
        lg="9"
        class="text-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Cele KPI</span>
        <h3 class="page-title">Lista celów KPI</h3>
      </d-col>
      <d-col
        col
        sm="12"
        md="3"
        lg="3"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/cele/nowy')">
          <i class="material-icons">add</i>
          Dodaj cel KPI
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-0 page-filters">
      <validation-observer
        ref="goals-filters-form"
        tag="form"
        class="d-flex mb-2">

        <v-select
          class="mr-3 mb-2 w-100"
          :options="allUsers"
          v-model="filters.userID"
          :reduce="(user) => user.id"
          placeholder="Wybierz użytkownika"
          :searchable="true"
          :filterBy="filterByForUsers"
          :disabled="!loadedUsers"
          label="name"
          @search="debouncedFetchUsers">
          <template slot="no-options">
            <template v-if="!emptyResults">Trwa wyszukiwanie&hellip;</template>
            <template v-else>Brak pracowników pasujących do podanej frazy&hellip;</template>
          </template>
          <template
            slot="option"
            slot-scope="option">
            <div class="d-center">
              {{ option.name }}
            </div>
          </template>
          <template
            slot="selected-option"
            slot-scope="option">
            <div class="selected d-center">
              {{ option.name }}
            </div>
          </template>
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-md-auto mr-1 mb-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap mb-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0 align-middle text-left">Pracownik</th>
            <th scope="col" class="border-0 align-middle text-right" style="width: 200px;">Kwota</th>
            <th scope="col" class="border-0 align-middle text-center">Termin wykonania</th>
            <th scope="col" class="border-0 align-middle text-center" style="width: 150px;">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(goal, index) of goals"
            :key="'goal-' + index">
            <td class="align-middle text-left py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Pracownik</label>
              {{ goal.user_name }}
            </td>
            <td class="align-middle text-right py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Kwota</label>
              {{ goal.amount }} zł
            </td>
            <td class="align-middle text-center py-lg-1 py-md-3 py-3">
              <label class="table-mobile-label mb-1">Termin wykonania</label>
              {{ goal.valid_from }}
            </td>

            <td class="text-center align-middle pt-0">
              <d-button
                theme="primary"
                @click.native="$router.push('/cele/edycja/' + goal.id)"
                class="mx-2 mt-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeGoal(goal.id)"
                class="mx-2 mt-2"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="goals.length === 0">
            <td
              colspan="4"
              class="text-center">
              Brak celów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się.
        <a
          href="javascript:window.location.reload();"
          class="alert-link">
          Odśwież stronę
        </a>
        aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from '../mixins/HasFilters.vue';
import HasPagination from '../mixins/HasPagination.vue';
import ListUtils from '../utils/ListUtils.js';
import FormUtils from '../utils/FormUtils.js';
import Pagination from '../components/common/Pagination.vue';

export default {
  name: 'goals',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      goals: [],
      allUsers: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      loadedUsers: false,
      emptyResults: false,
      filters: {
        userID: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    loadFilteredData () {
      let where = {};

      if (this.filters.userID) {
        where.user_id = this.filters.userID;
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: '/api/kpi-schedules/items?page=' + this.currentPage,
        listField: 'goals',
        pagination: true,
        params: {
          where
        }
      });
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/users/items',
        method: 'get',
        outputKey: 'allUsers',
        loadedKey: 'loadedUsers',
        errorKey: 'loadError'
      });
    },
    removeGoal (goalID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/kpi-schedules/delete/',
        id: goalID,
        successTitle: 'Usunięto cel',
        successText: 'Wybrany cel został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten cel?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie celu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    clearFilters() {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        userID: null
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm() {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(
        this.$refs['goals-filters-form'],
        this.debouncedLoadFilteredData
      );
    },
    filterByForUsers (option, label, search) {
      return (option.name).toLocaleLowerCase('pl').indexOf(search.toLocaleLowerCase('pl')) > -1;
    },
    fetchUsers (searchPhrase, loadingCallback) {
      this.emptyResults = false;
      loadingCallback(true);

      let endpointUrl = '/api/users/items';
      let responseAsArray = false;
      let paramsData = {};

      if (searchPhrase !== '') {
        paramsData.where = {
          search: searchPhrase
        };
      }

      if (this.filters.workerID !== 0 && this.filters.workerID !== null && searchPhrase === '') {
        endpointUrl = '/api/users/item/' + parseInt(this.filters.workerID, 10);
        responseAsArray = true;
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: endpointUrl,
        outputKey: 'allUsers ',
        errorKey: 'loadError',
        params: paramsData,
        wrapResponseIntoArray: responseAsArray,
        successAction: (response) => {
          if (!response.length) {
            this.emptyResults = true;
          }

          loadingCallback(false);
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    // eslint-disable-next-line
    debouncedFetchUsers: debounce(function (searchPhrase, loadingCallback) {
      this.fetchWorkers(searchPhrase, loadingCallback);
    }, 500),
  }
};

</script>
